// Building-scoped KB last/next run — latest tick finished_at + estimated next = +15 min.
// Shown on Sisäilmaolosuhteet / Datan laatu when a single building is selected (#973).
import { useEffect, useState } from "react";
import { listKBTickSummaries, type KBTickSummary } from "@/lib/api/agent";
import { cardToneText } from "@/lib/card-styles";
import {
  deriveKbRunTiming,
  formatCountdown,
  formatLastRunFi,
  type KbRunTiming,
} from "./kb-run-timing";

type LoadState =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; finishedAt: string | null };

function useNowTick(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
}

export function KbRunIndicator({
  buildingId,
  ticks,
}: {
  buildingId: string;
  /** When set (Energiatehokkuus building snapshot), skip the tick-list HTTP. */
  ticks?: KBTickSummary[];
}) {
  const ticksFingerprint =
    ticks === undefined
      ? null
      : ticks.map((t) => `${t.tick_id}:${t.finished_at}`).join(",");
  const [load, setLoad] = useState<LoadState>(() =>
    ticks !== undefined
      ? { kind: "ready", finishedAt: ticks[0]?.finished_at ?? null }
      : { kind: "loading" },
  );
  const now = useNowTick(load.kind === "ready");

  useEffect(() => {
    if (ticksFingerprint != null && ticks !== undefined) {
      setLoad({ kind: "ready", finishedAt: ticks[0]?.finished_at ?? null });
      return;
    }
    let cancelled = false;
    setLoad({ kind: "loading" });
    listKBTickSummaries(buildingId)
      .then((res) => {
        if (cancelled) return;
        // API returns most recent first.
        const latest = res.ticks[0]?.finished_at ?? null;
        setLoad({ kind: "ready", finishedAt: latest });
      })
      .catch(() => {
        if (!cancelled) setLoad({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, ticksFingerprint]);

  if (load.kind === "loading") {
    return <div className="text-sm text-muted-foreground">Ladataan ajoa…</div>;
  }
  if (load.kind === "error") {
    return (
      <div className="text-sm text-muted-foreground">Ajoa ei voitu ladata</div>
    );
  }

  const timing = deriveKbRunTiming(load.finishedAt, now);
  return <KbRunTimingView timing={timing} />;
}

function KbRunTimingView({ timing }: { timing: KbRunTiming }) {
  if (timing.status === "never") {
    return <div className="text-sm text-muted-foreground">Ei vielä ajettu</div>;
  }

  const last = formatLastRunFi(timing.lastFinishedAt);
  if (timing.status === "overdue") {
    return (
      <div className="text-sm text-muted-foreground tabular-nums">
        Ajettu {last} · seuraava{" "}
        <span className={cardToneText.warn}>myöhässä</span>
      </div>
    );
  }

  const { mm, ss } = formatCountdown(timing.remainingMs);
  return (
    <div className="text-sm text-muted-foreground tabular-nums">
      Ajettu {last} · seuraava {mm}:{ss}
    </div>
  );
}
